import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * Autopilot banner — visible while the pathfinding autopilot is flying the
 * ship. Click to cancel; any direction or special input (keyboard, gamepad or
 * touch) also cancels, while firing doesn't.
 *
 * @element autopilot-banner
 */
@customElement('autopilot-banner')
export class AutoPilotBanner extends LitElement {
  static override styles = [
    tokens,
    css`
      /* Placed by airmash-game's .status-stack (bottom centre, under the
         prowler alert). display: contents, so the host adds no box of its own:
         with nothing to show, the stack skips it entirely. */
      :host {
        display: contents;
        /* Autopilot accent: component-local, deliberately not a theme token —
           it's used nowhere else. RGB channels so each use sets its own alpha. */
        --autopilot-rgb: 20, 130, 200;
        /* Declared here rather than inherited from the body — the component
           owns its own typography and its click-to-cancel behaviour. */
        font-family: var(--font-family);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
      }

      .banner {
        padding: 6px 14px;
        background-color: rgba(var(--autopilot-rgb), 0.75);
        border-radius: var(--radius-sm);
        color: var(--color-text-white);
        font-size: var(--font-size-13);
        font-weight: bold;
        letter-spacing: 0.1em;
        text-align: center;
        text-shadow: var(--shadow-text);
        box-shadow: 0 0 8px rgba(var(--autopilot-rgb), 0.6);
        cursor: pointer;
        /* airmash-ui is click-through (pointer-events: none, inherited), so a
           clickable piece has to opt back in. Without this, clicks fell through
           to the canvas: no cancel, and in mouse mode a fired missile. */
        pointer-events: auto;
        animation: autopilot-pulse 1.8s ease-in-out infinite;
      }

      /* It's a button: hovering holds the pulse at full strength and firms up
         the blue. Gated, so a touch can't leave it stuck lit. */
      @media (hover: hover) {
        .banner:hover {
          animation: none;
          background-color: rgba(var(--autopilot-rgb), 0.9);
        }
      }

      .hint {
        display: block;
        margin-top: 2px;
        font-size: var(--font-size-xxs);
        font-weight: normal;
        letter-spacing: 0.05em;
        opacity: 0.85;
      }

      @keyframes autopilot-pulse {
        0%,
        100% {
          opacity: 0.85;
        }
        50% {
          opacity: 1;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  private handleCancel = () => {
    this.api.playClick();
    this.api.cancelAutoPilot();
  };

  override render() {
    if (!this.uiState.autopilotActive) return nothing;
    return html`
      <div class="banner" @click=${this.handleCancel}>
        AUTOPILOT
        <span class="hint">click or press any direction to cancel</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'autopilot-banner': AutoPilotBanner;
  }
}
